/**
 * "Auto-order skipped — already ordered" derivation (PROD-1037 follow-up).
 *
 * The runner no longer double-books a student whose (day, mealtime) is
 * already covered by a hand-placed order — but the skip must not be silent:
 * the family calendar (week day cards) and the day view show a subtle
 * caption explaining WHY no auto-order landed.
 *
 * A mealtime counts as skipped for a child on a date when ALL hold:
 *   - the child's subscription at THIS school is ENABLED, targets that
 *     mealtime, fires on that weekday (picks), isn't parent-skipped for the
 *     date, isn't paused past it, and the date is inside its effective
 *     window — mirroring the runner's firing gates for weekly FAMILY rules;
 *   - the child has live ordered quantity at that (date, mealtime);
 *   - none of that quantity came from an auto-placed (subscription-stamped)
 *     batch — if the auto-order itself placed, nothing was skipped.
 *
 * Deliberately NOT modeled: school holidays / out-of-session days (a parent
 * cannot hand-order those — no menu — so the coverage leg never holds) and
 * biweekly INTERVAL rules (the family wizard only writes weekly picks).
 * Pure; exported for tests.
 */

import type { FamilySubscriptionWire } from '@/api/family-subscriptions'
import type { Mealtime } from '@/api/school-orders'

import type { FamilyAlreadyOrdered } from './already-ordered'

/** UTC weekday for a YYYY-MM-DD key (0=Sun … 6=Sat). */
function weekdayOf(dateKey: string): number {
  return new Date(`${dateKey}T00:00:00.000Z`).getUTCDay()
}

/**
 * Would this subscription's auto-order fire for `dateKey`? Mirrors the
 * runner's FAMILY gates at weekly-pick granularity.
 */
export function subscriptionFiresOn(sub: FamilySubscriptionWire, dateKey: string): boolean {
  if (sub.isArchived || sub.status !== 'ENABLED') {
    return false
  }
  if (!sub.picks.some((pick) => pick.weekday === weekdayOf(dateKey))) {
    return false
  }
  if (sub.skippedDates.includes(dateKey)) {
    return false
  }
  const date = new Date(`${dateKey}T00:00:00.000Z`).getTime()
  if (sub.pauseUntil && new Date(sub.pauseUntil).getTime() > date) {
    return false
  }
  if (sub.effectiveFrom && new Date(sub.effectiveFrom).getTime() > date) {
    return false
  }
  if (sub.effectiveTo && new Date(sub.effectiveTo).getTime() < date) {
    return false
  }
  return true
}

/**
 * The mealtimes on `dateKey` where the child's auto-order stood down because
 * the slot was already hand-ordered. At most one entry today (a child holds
 * one subscription, one mealtime) — returned as a list so the callers render
 * uniformly beside the per-mealtime warnings.
 */
export function autoOrderSkippedMealtimes(args: {
  dateKey: string
  studentUserId: string
  /** The child's best subscription at the school being viewed (or none). */
  subscription: FamilySubscriptionWire | undefined
  alreadyOrdered: FamilyAlreadyOrdered
}): Mealtime[] {
  const { dateKey, studentUserId, subscription, alreadyOrdered } = args
  if (!subscription || !subscriptionFiresOn(subscription, dateKey)) {
    return []
  }
  const mealtime = subscription.mealtime
  if (alreadyOrdered.mealtimeStudentQty(dateKey, mealtime, studentUserId) <= 0) {
    return []
  }
  if (alreadyOrdered.hasAutoOrder(dateKey, mealtime, studentUserId)) {
    return []
  }
  return [mealtime]
}
